import React, { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import shortid from 'shortid'
import numeral from 'numeraljs'

import { Table, Body, Head, HeaderCell, Cell, BodyItem } from './row'
import styles from '../component-styles/current-team-mates.module.scss'
import { GraphQLState } from '../store/graphql-reducer'
import { SiteState } from '../store/site-reducer'
import {
  getUserListAsync,
  getUserListGQL,
  logActionAsync,
  removeUserAsync,
  revokeEmailInviteAsync,
  updateUserPermissionsAsync,
} from '../store/actions'
import PermissionSelector from './permission-selector'
import ResendInvitationButton from './resend-invitation-button'
import DeleteButtonWithConfirmation from './delete-button-with-confirmation'
import { dateFormatShort } from '../core/variables'
import useTableSortFilter, {
  SearchFn,
  SortFn,
} from '../hooks/useTableSortFilter'
import Button, { ButtonRow } from './button'
import OrderArrow from './order-arrow'
import Pagination from './pagination'
import HeaderPanel, { Panel } from './header-panel'
import { SearchInput } from './input'
import { isExistingUser, isPendingUser } from '../helpers/type-guards'
import { getCsvString, downloadUsersCsv } from '../helpers'
import { Preloader } from './loader'

interface TableRowProps {
  email?: string
  lastLogin?: string
  permission: PermissionLevel
  userID: string
  index: number
  onChange: any
  onDelete: any
  uid?: string
}

export function TableRow({
  email = '',
  permission,
  lastLogin = '',
  userID,
  index,
  onChange,
  onDelete,
  uid,
}: TableRowProps) {
  return (
    <BodyItem>
      <Cell width={540}>
        <span className={styles.emailSummary}>
          <h3>{email}</h3>
          <p>{permission}</p>
        </span>
      </Cell>
      <Cell width={140}>
        <p className={styles.date}>
          {(lastLogin &&
            lastLogin !== '' &&
            moment(lastLogin).format(dateFormatShort)) ||
            'Unknown'}
        </p>
      </Cell>
      <Cell width={156}>
        {uid !== userID &&
          permission !== 'support' &&
          permission !== 'billing' && (
            <PermissionSelector
              className={styles.permissionSelector}
              permission={permission}
              onChange={(permissionLevel) => {
                onChange(userID, permissionLevel)
              }}
            />
          )}
      </Cell>
      <Cell width={65}>
        {uid !== userID && permission !== 'support' && (
          <DeleteButtonWithConfirmation
            odd={!(index % 2)}
            onClick={() => {
              onDelete(userID)
            }}
          >
            <p>
              Remove <strong>{email}</strong>?
            </p>
          </DeleteButtonWithConfirmation>
        )}
      </Cell>
    </BodyItem>
  )
}

interface TableRowPendingProps {
  email: string
  sentTime?: string
  expired?: boolean
  permissionLevel: PermissionLevel
  index: number
  onDelete: (email: string) => void
  accountID: string
}

export function TableRowPending({
  accountID,
  email,
  expired,
  sentTime,
  permissionLevel,
  index,
  onDelete,
}: TableRowPendingProps) {
  return (
    <BodyItem>
      <Cell width={540}>
        <span className={styles.emailSummary}>
          <h3>{email}</h3>
          <p>{expired ? 'invitation has expired' : 'Pending invitation'}</p>
        </span>
      </Cell>
      <Cell width={140}>
        <p className={styles.date}>
          {(sentTime && moment(sentTime).format(dateFormatShort)) || 'Unknown'}
        </p>
      </Cell>
      <Cell width={156}>
        <ResendInvitationButton
          accountID={accountID}
          email={email}
          permissionLevel={permissionLevel}
        />
      </Cell>
      <Cell width={65}>
        <DeleteButtonWithConfirmation
          odd={!(index % 2)}
          onClick={() => {
            onDelete(email)
          }}
        >
          <p>
            Revoke invitation for <strong>{email}</strong>?
          </p>
        </DeleteButtonWithConfirmation>
      </Cell>
    </BodyItem>
  )
}

export default function CurrentTeamMates() {
  const dispatch = useDispatch()

  const {
    graphql,
    site: { selected, loadingUserList },
  }: {
    graphql: GraphQLState
    site: SiteState
  } = useSelector((s: any): any => s)

  const [userListRequested, setUserListRequested] = useState(false)

  const accountName = selected?.accountName || ''

  useEffect(() => {
    if (!userListRequested) {
      dispatch(getUserListAsync())
      dispatch(getUserListGQL())
      setUserListRequested(true)
    }
  }, [])

  const { currentUser, currentAccount } = graphql
  const accountID = selected?.accountID || ''
  const onChange = async (userID, permissionLevel) => {
    if (!userID || !permissionLevel || !currentAccount?.accountID) {
      return
    }
    if (permissionLevel !== 'admin' && permissionLevel !== 'regular') return
    dispatch(
      updateUserPermissionsAsync({
        accountID: currentAccount?.accountID,
        permissionLevel,
        userID,
      }),
    )
  }

  const onDelete = async (userID) => {
    if (!userID || !currentAccount?.accountID) {
      return
    }
    dispatch(
      removeUserAsync({
        accountID: currentAccount?.accountID,
        userID,
      }),
    )
  }

  const revokeInvitation = async (email: string) => {
    dispatch(
      revokeEmailInviteAsync({
        email,
      }),
    )
  }

  const permissionSort: SortFn<
    UserWithPermission | PendingUserWithPermission
  > = useCallback(
    (user) => (isExistingUser(user) ? user.permission : user.permissionLevel),
    [selected],
  )

  const lastLoginSort: SortFn<
    UserWithPermission | PendingUserWithPermission
  > = useCallback(
    (user) => {
      if (isExistingUser(user) && user.lastLogin) {
        return new Date(user.lastLogin)
      }
      if (isPendingUser(user)) {
        return new Date(user.sentTime)
      }
      return ''
    },
    [selected],
  )

  const permissionSearch: SearchFn<
    (UserWithPermission | PendingUserWithPermission)[]
  > = useCallback(
    (data, search = '') => {
      const searchPattern = search
        .replace(/[^a-zA-Z0-9]/g, '')
        .split('')
        .join('.*')

      return data.concat().filter((user) => {
        return (
          (isExistingUser(user) &&
            user.permission
              .toLowerCase()
              .match(new RegExp(searchPattern, 'i')) !== null) ||
          (isPendingUser(user) &&
            user.permissionLevel
              .toLowerCase()
              .match(new RegExp(searchPattern, 'i')) !== null)
        )
      })
    },
    [selected],
  )

  const lastUsedSearch: SearchFn<
    (UserWithPermission | PendingUserWithPermission)[]
  > = useCallback(
    (data, search = '') => {
      const searchPattern = search
        .replace(/[^a-zA-Z0-9 -\\/]/g, '')
        .split(' ')
        .join('.*')

      return data.concat().filter((user) => {
        let date = ''
        if (isPendingUser(user)) {
          date = moment(user.sentTime).format(
            'dddd Do MMMM YYYY, DD-MM-YYYY, DD/MM/YYYY',
          )
        } else {
          date = user.lastLogin
            ? moment(user.lastLogin).format(
                'dddd Do MMMM YYYY, DD-MM-YYYY, DD/MM/YYYY',
              )
            : ''
        }

        return date.toLowerCase().match(new RegExp(searchPattern, 'i')) !== null
      })
    },
    [selected],
  )

  const {
    initialSort,
    setInitialSort,
    orderAsc,
    sortKey,
    setSortOrder,
    pages,
    activePage,
    setActivePage,
    setSelectedSearchType,
    setSearchTerm,
    count,
    orderedList: orderedPermissionList,
    total,
  } = useTableSortFilter(
    selected?.permissionList,
    'lastLogin',
    '',
    25,
    {
      lastUsed: lastUsedSearch,
      permission: permissionSearch,
    },
    {
      permission: permissionSort,
      lastLogin: lastLoginSort,
    },
    false,
    selected?.pendingUserList,
  )

  const transformUserList = (
    existing: UserWithPermission[],
    pending?: PendingUserWithPermission[],
  ): TransformedUser[] => {
    let fullList: (
      | UserWithPermission
      | PendingUserWithPermission
    )[] = JSON.parse(JSON.stringify(existing))

    if (pending) {
      fullList = fullList.concat(JSON.parse(JSON.stringify(pending)))
    }

    const transformedList = fullList.map((user) => {
      let lastLogin: string | Date = ''
      let regDate: string | Date = ''

      if (isExistingUser(user)) {
        if (user.lastLogin) {
          lastLogin = moment(new Date(user.lastLogin)).format(
            'YYYY-MM-DD HH:MM:SS',
          )
        }
        if (user.created) {
          regDate = moment(new Date(user.created)).format('YYYY-MM-DD HH:MM:SS')
        }
      }

      return {
        Account: accountName,
        Email: user.email || '',
        'Account permission': isExistingUser(user)
          ? user.permission
          : user.permissionLevel,
        'Last login date': lastLogin,
        'Date registered': regDate,
        'Pending registration': !isExistingUser(user),
        'Date registration sent': isExistingUser(user)
          ? ''
          : moment(new Date(user.sentTime)).format('YYYY-MM-DD HH:MM:SS'),
        'Registration link expired': isExistingUser(user) ? '' : user.expired,
      }
    })

    return transformedList.filter(
      (user) => user['Account permission'] !== 'support',
    )
  }

  interface UsersDownloadInteraction {
    numberOfUsers: number
    numberOfAdmin: number
    numberOfRegular: number
    numberPending: number
    numberExpired: number
  }

  const getDetails = (
    userList: TransformedUser[],
  ): UsersDownloadInteraction => {
    return {
      numberOfUsers: userList.length,
      numberOfAdmin: userList.filter(
        (user) => user['Account permission'] === 'admin',
      ).length,
      numberOfRegular: userList.filter(
        (user) => user['Account permission'] === 'regular',
      ).length,
      numberPending: userList.filter(
        (user) =>
          user['Pending registration'] && !user['Registration link expired'],
      ).length,
      numberExpired: userList.filter(
        (user) => user['Registration link expired'],
      ).length,
    }
  }

  return (
    <div className={styles.subsection}>
      <HeaderPanel className={styles.tableHeader}>
        <Panel className={styles.searchRow}>
          <ButtonRow style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <SearchInput
                onChange={(value) => {
                  setSearchTerm(value)
                  setActivePage(1)
                }}
                delay={50}
                loading={loadingUserList}
                searchTypeList={[
                  { name: 'All', value: 'all' },
                  {
                    name: 'User',
                    value: 'email',
                  },
                  {
                    name: 'Last used',
                    value: 'lastUsed',
                  },
                  {
                    name: 'Role',
                    value: 'permission',
                  },
                ]}
                onChangeSearchType={(type: string) => {
                  setSelectedSearchType(type)
                }}
                loadingLabel="Counting users"
              >
                <>
                  {count !== total ? `${numeral(count).format('0,0')}/` : ''}
                  {numeral(total).format('0,0')} user{total > 1 ? 's' : ''}
                </>
              </SearchInput>
            </div>
            {!loadingUserList && (
              <Button
                type="flatPink"
                download
                className={styles.downloadButton}
                onClick={async () => {
                  if (selected && selected.permissionList) {
                    const userList = transformUserList(
                      selected.permissionList,
                      selected.pendingUserList,
                    )
                    const csv = getCsvString(userList)

                    const interactionDetails = JSON.stringify(
                      getDetails(userList),
                    )

                    await downloadUsersCsv(csv, accountName)

                    dispatch(
                      logActionAsync({
                        action: 'download-user-list-csv',
                        longDescription: interactionDetails,
                        websiteSection: 'settings',
                      }),
                    )
                  }
                }}
              >
                Download users
              </Button>
            )}
          </ButtonRow>
        </Panel>
      </HeaderPanel>
      <Table className={styles.table}>
        <Head>
          <HeaderCell width={540} className={styles.headerCellWithButton}>
            <Button
              className={styles.headerButton}
              onClick={(): void => {
                if (initialSort) {
                  setInitialSort(false)
                }
                setSortOrder('email')
              }}
              type="none"
            >
              User
              <OrderArrow
                currentKey="email"
                sortKey={sortKey}
                orderAsc={orderAsc}
              />
            </Button>
          </HeaderCell>
          <HeaderCell width={140}>
            <Button
              className={styles.headerButton}
              onClick={(): void => {
                if (initialSort) {
                  setInitialSort(false)
                }
                setSortOrder('lastLogin')
              }}
              type="none"
            >
              Last login
              <OrderArrow
                currentKey="lastLogin"
                sortKey={sortKey}
                orderAsc={orderAsc}
              />
            </Button>
          </HeaderCell>
          <HeaderCell width={156}>
            <Button
              className={styles.headerButton}
              onClick={(): void => {
                if (initialSort) {
                  setInitialSort(false)
                }
                setSortOrder('permission')
              }}
              type="none"
            >
              Role
              <OrderArrow
                currentKey="permission"
                sortKey={sortKey}
                orderAsc={orderAsc}
              />
            </Button>
          </HeaderCell>
          <HeaderCell width={65} />
        </Head>
        <Body>
          {loadingUserList ? (
            <Preloader />
          ) : (
            <>
              {orderedPermissionList &&
                orderedPermissionList[activePage - 1] &&
                orderedPermissionList[activePage - 1].map(
                  (user, index: number) => {
                    if (isExistingUser(user)) {
                      return (
                        <TableRow
                          uid={currentUser?.userID || ''}
                          key={shortid.generate()}
                          index={index}
                          onChange={onChange}
                          onDelete={onDelete}
                          email={user.email}
                          permission={user.permission}
                          lastLogin={user.lastLogin}
                          userID={user.userID}
                        />
                      )
                    }
                    return (
                      <TableRowPending
                        key={shortid.generate()}
                        index={index}
                        onDelete={revokeInvitation}
                        email={user.email}
                        sentTime={user.sentTime}
                        expired={user.expired}
                        permissionLevel={user.permissionLevel}
                        accountID={accountID}
                      />
                    )
                  },
                )}
            </>
          )}
        </Body>
      </Table>
      {pages > 1 && (
        <ButtonRow className={styles.paginationRow}>
          <Pagination
            pages={pages}
            activePage={activePage}
            onChange={(index: number) => {
              setActivePage(index)
            }}
          />
        </ButtonRow>
      )}
    </div>
  )
}
